/**
 * Camada de acesso aos 4 endpoints públicos da extensão (spec §5).
 *
 * Todas as respostas seguem o envelope `{ data: T }` do backend, igual ao que
 * MissingDataForm já consome. As validações acontecem antes da requisição: é
 * mais barato barrar um arquivo grande ou um payload incompleto aqui do que
 * depender do erro do servidor, e o titular recebe mensagem em português.
 */

import api from "services/api";

import { buildPreviewUpload, isPreviewToken } from "./devPreview";
import {
  ACCEPTED_DOCUMENT_MIME_TYPES,
  IExtensionFormState,
  IResolveResponse,
  ISubmitPayload,
  IUploadedDocument,
  MAX_DOCUMENT_SIZE_BYTES,
  MAX_DOCUMENTS_TOTAL_SIZE_BYTES,
  PersonType,
} from "./types";

/** Falha de validação no cliente, antes de qualquer chamada de rede. */
export class ExtensionValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ExtensionValidationError";
  }
}

/**
 * invariant: os dois segmentos vêm do link do e-mail, e o `organizationId` é o
 * que diz ao backend em que conta Snowflake procurar o token. Perder um deles
 * deixa o formulário inalcançável, que foi o estado até 20/09/2026.
 */
export function extensionBasePath(organizationId: string, token: string): string {
  if (!organizationId) {
    throw new ExtensionValidationError("Organização da campanha ausente.");
  }

  if (!token) throw new ExtensionValidationError("Token da campanha ausente.");

  return `/extensao/${encodeURIComponent(organizationId)}/${encodeURIComponent(token)}`;
}

/**
 * Carrega o contexto da oferta na entrada da tela. Não marca o token como
 * consumido (spec §5), então pode ser refeito se o titular recarregar a página.
 */
export async function resolveExtensionForm(
  organizationId: string,
  token: string
): Promise<IResolveResponse> {
  const { data } = await api.get<{ data: IResolveResponse }>(
    `${extensionBasePath(organizationId, token)}/resolve`
  );

  return data.data;
}

/** Recusa no Step 0. Registra o aceite LGPD e consome o token. */
export async function declineExtension(
  organizationId: string,
  token: string
): Promise<void> {
  if (isPreviewToken(token)) return;

  await api.post(`${extensionBasePath(organizationId, token)}/decline`, {
    lgpd_aceite: true,
  });
}

interface IUploadParams {
  fieldId: string;
  personType: PersonType;
  /** CPF do titular ou do dependente a quem o documento pertence. */
  beneficiaryTaxPayerId: string;
  file: File;
  /** Soma dos anexos já enviados, para respeitar o limite por candidato. */
  currentTotalSizeBytes: number;
}

function formatMegabytes(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))}MB`;
}

/**
 * Envia um anexo imediatamente, como o design exige (o botão "Anexar" vira
 * "Ver arquivo" na hora). Valida tipo e tamanho antes de subir.
 */
export async function uploadExtensionDocument(
  organizationId: string,
  token: string,
  {
    fieldId,
    personType,
    beneficiaryTaxPayerId,
    file,
    currentTotalSizeBytes,
  }: IUploadParams
): Promise<IUploadedDocument> {
  const isAcceptedType = (
    ACCEPTED_DOCUMENT_MIME_TYPES as readonly string[]
  ).includes(file.type);

  if (!isAcceptedType) {
    throw new ExtensionValidationError(
      "Formato não aceito. Envie um arquivo PDF, JPEG ou PNG."
    );
  }

  if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
    throw new ExtensionValidationError(
      `Arquivo maior que ${formatMegabytes(MAX_DOCUMENT_SIZE_BYTES)}.`
    );
  }

  if (currentTotalSizeBytes + file.size > MAX_DOCUMENTS_TOTAL_SIZE_BYTES) {
    throw new ExtensionValidationError(
      `Limite total de ${formatMegabytes(
        MAX_DOCUMENTS_TOTAL_SIZE_BYTES
      )} em anexos excedido.`
    );
  }

  if (isPreviewToken(token)) {
    return buildPreviewUpload({
      fieldId,
      personType,
      beneficiaryTaxPayerId,
      file,
    });
  }

  const body = new FormData();
  body.append("field_id", fieldId);
  body.append("person_type", personType);
  body.append("beneficiary_cpf", beneficiaryTaxPayerId);
  body.append("file", file);

  const { data } = await api.post<{ data: IUploadedDocument }>(
    `${extensionBasePath(organizationId, token)}/documents`,
    body,
    { headers: { "Content-Type": "multipart/form-data" } }
  );

  return data.data;
}

/**
 * Converte o estado do wizard no objeto que o backend espera, em snake_case
 * (spec §5). Centraliza a montagem num único lugar para o payload não divergir
 * do contrato conforme a UI muda.
 */
export function buildSubmitPayload(state: IExtensionFormState): ISubmitPayload {
  if (!state.hasAcceptedRn561) {
    throw new ExtensionValidationError(
      "É necessário aceitar os termos da RN 561 para enviar."
    );
  }

  if (!state.chosenPlanId) {
    throw new ExtensionValidationError("Nenhum plano foi selecionado.");
  }

  const personalEmail = state.contact.personalEmail.trim();
  const mobilePhone = state.contact.mobilePhone.replace(/\D/g, "");

  if (!personalEmail) {
    throw new ExtensionValidationError("Informe o e-mail pessoal.");
  }

  if (!mobilePhone) {
    throw new ExtensionValidationError("Informe o telefone celular.");
  }

  return {
    confirmed_email: personalEmail,
    confirmed_phone: mobilePhone,
    retained_dependent_cpfs: [...state.retainedDependentTaxPayerIds],
    chosen_plan_id: state.chosenPlanId,
    uploaded_document_keys: state.uploadedDocuments.map(
      (document) => document.s3Key
    ),
    rn561_aceite: true,
  };
}

/** Envio final. Consome o token e dispara as transições de status no backend. */
export async function submitExtensionResponse(
  organizationId: string,
  token: string,
  state: IExtensionFormState
): Promise<void> {
  const payload = buildSubmitPayload(state);

  if (isPreviewToken(token)) return;

  await api.post(`${extensionBasePath(organizationId, token)}/submit`, payload);
}
